import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import * as Sentry from '@sentry/vue';
import { authApi } from '@/api';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null);
  const loading = ref(false);
  const initialized = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!user.value);
  const isApproved = computed(() => user.value?.status === 'approved');
  const isPending = computed(() => user.value?.status === 'pending');
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'root');
  const isRoot = computed(() => user.value?.role === 'root');
  const isImpersonating = computed(() => user.value?.impersonating === true);
  const originalUser = computed(() => user.value?.originalUser || null);

  // Actions
  async function fetchUser() {
    loading.value = true;
    try {
      const data = await authApi.getMe();
      user.value = data; // API returns user object directly
      Sentry.setUser({
        // /auth/me exposes the id as `id`, not `_id`.
        id: data.id || data._id,
        username: data.username || data.displayName,
      });
    } catch {
      user.value = null;
      Sentry.setUser(null);
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function logout() {
    const data = await authApi.logout();
    if (data.restored) {
      user.value = data.user;
      return;
    }
    user.value = null;
    Sentry.setUser(null);
  }

  async function impersonateUser(userId) {
    const data = await authApi.impersonate(userId);
    user.value = data;
  }

  async function stopImpersonating() {
    const data = await authApi.stopImpersonating();
    user.value = data;
  }

  function loginWithGitHub() {
    window.location.href = authApi.getGitHubAuthUrl();
  }

  async function loginAsDevUser(userId) {
    const data = await authApi.devLogin(userId);
    user.value = data;
    Sentry.setUser({
      id: data._id || data.id,
      username: data.username || data.displayName,
    });
  }

  return {
    // State
    user,
    loading,
    initialized,
    // Getters
    isAuthenticated,
    isApproved,
    isPending,
    isAdmin,
    isRoot,
    isImpersonating,
    originalUser,
    // Actions
    fetchUser,
    logout,
    loginWithGitHub,
    loginAsDevUser,
    impersonateUser,
    stopImpersonating,
  };
});
